import { Component, OnInit, Renderer2, OnDestroy } from '@angular/core';
import { ProductService } from '../service/product.service';
import { Product } from '../Module/Product.model';
import { DeleteConfirmationDialogComponent } from '../delete-confirmation-dialog/delete-confirmation-dialog.component';
import { PrintBarcodeDialogComponent } from '../print-barcode-dialog/print-barcode-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { map, Observable, of } from 'rxjs';
import { StateService } from '../service/state.service';
import { Router } from '@angular/router';

@Component({
  selector: 'app-product-list',
  templateUrl: './product-list.component.html',
  styleUrls: ['./product-list.component.css']
})
export class ProductListComponent implements OnInit, OnDestroy {

  products: Product[] = [];
  originalProducts: Product[] = [];
  currentPage = 1;
  productsPerPage = 100;
  productSize!: number;
  pagedItems: Product[] = [];
  pages: number[] = [];
  pagedItemsSize!: number;
  selectedStockStatus: string = '';
  query: string = '';
  isLoading: boolean = false;

  // Computed properties for dashboard stats
  inStockCount: number = 0;
  lowStockCount: number = 0;
  outOfStockCount: number = 0;

  constructor(
    private productService: ProductService,
    private dialog: MatDialog,
    private renderer: Renderer2,
    private stateService: StateService,
    private router: Router
  ) { }

  ngOnInit(): void {
    this.isLoading = true;
    this.productService.listProducts().subscribe({
      next: (data: Product[]) => {
        this.products = data;
        this.originalProducts = [...data];
        this.productSize = this.products.length;
        this.setPage(this.currentPage);
        this.pages = this.gettotalPages();
        this.renderer.addClass(document.body, 'your-component-bg');
        this.isLoading = false;

        console.log('💰 [CAPITAL] Total capital:', this.calculateTotal1(this.products));

        // Calculate stats counts
        this.updateStatsCounts();
      },
      error: (error) => {
        console.error('❌ [PRODUCT-LIST] Failed to load products:', error);
        this.isLoading = false;
      }
    });
  }

  // Method to update the stats counts
  updateStatsCounts(): void {
    this.inStockCount = this.products.filter(p => this.isProductInStock(p)).length;
    this.lowStockCount = this.products.filter(p => this.isProductIsLowStock(p)).length;
    this.outOfStockCount = this.products.filter(p => this.isProuctOutOfStock(p)).length;
  }

  ngOnDestroy(): void {
    this.renderer.removeClass(document.body, 'your-component-bg');
  }

  calculateTotal1(products: Product[]): number {
    return products.reduce((total, product) =>
      total + (product.purchasePrice ?? 0) * (product.quantity ?? 0), 0);
  }

  filterProducts(): void {
    if (!this.query && !this.selectedStockStatus) {
      this.products = [...this.originalProducts];
    } else {
      this.searchOrders(this.query, this.selectedStockStatus).subscribe(products => {
        this.products = products;
      });
    }
    this.productSize = this.products.length;
    this.pages = this.gettotalPages();
    this.setPage(1);

    // Update stats counts after filtering
    this.updateStatsCounts();
  }

  searchOrders(query: string, stockStatus: string): Observable<Product[]> {
    return this.gertAllProducts().pipe(
      map(products => products.filter(product => {
        const normalizeString = (str: string) =>
          str.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
        const lowerCaseQuery = normalizeString(query);
        const matchesQuery = (product.name && normalizeString(product.name).includes(lowerCaseQuery)) ||
          (product.description && normalizeString(product.description).includes(lowerCaseQuery)) ||
          (product.codeBare && product.codeBare.toString().includes(lowerCaseQuery)) ||
          lowerCaseQuery.split(' ').every(term =>
            (product.name && normalizeString(product.name).includes(term)) ||
            (product.description && normalizeString(product.description).includes(term))
          );
        const matchesStockStatus = this.filterByStockStatus(product, stockStatus);
        return matchesQuery && matchesStockStatus;
      }))
    );
  }

  filterByStockStatus(product: Product, stockStatus: string): boolean {
    switch (stockStatus) {
      case 'faibleStock':
        return (product.quantity ?? 0) > 0 && (product.quantity ?? 0) <= 2;
      case 'enRuptureDeStock':
        return product.quantity === 0;
      case 'enStock':
        return (product.quantity ?? 0) > 2;
      default:
        return true;
    }
  }

  gertAllProducts(): Observable<Product[]> {
    return of(this.originalProducts);
  }

  onInputChange(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    this.query = inputElement.value;
    this.filterProducts();
  }

  onStockStatusChange(event: Event): void {
    const selectElement = event.target as HTMLSelectElement;
    this.selectedStockStatus = selectElement.value;
    this.filterProducts();
  }

  gettotalPages(): number[] {
    const totalPages = Math.ceil(this.productSize / this.productsPerPage);
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.setPage(page);
  }

  isProductInStock(product?: Product): boolean {
    return (product?.quantity ?? 0) > 2;
  }

  isProuctOutOfStock(product?: Product): boolean {
    return (product?.quantity ?? 0) === 0;
  }

  isProductIsLowStock(product?: Product): boolean {
    return (product?.quantity ?? 0) <= 2 && (product?.quantity ?? 0) > 0;
  }

  setPage(page: number): void {
    this.currentPage = page;
    const startIndex = (page - 1) * this.productsPerPage;
    const endIndex = startIndex + this.productsPerPage;
    this.pagedItems = this.products.slice(startIndex, endIndex);
    this.pagedItemsSize = this.pagedItems.length;
  }

  // Delete Confirmation Logic
  showDeleteConfirmation: boolean = false;
  productToDelete: Product | null = null;

  openDeleteConfirmation(product: Product): void {
    this.productToDelete = product;
    this.showDeleteConfirmation = true;
  }

  confirmDelete(): void {
    if (this.productToDelete) {
      this.isLoading = true;
      this.productService.deleteProduct(this.productToDelete.id!).subscribe({
        next: () => {
          this.isLoading = false;
          this.showDeleteConfirmation = false;
          this.productToDelete = null;
          // Refresh list
          this.products = this.products.filter(p => p.id !== this.productToDelete?.id);
          this.originalProducts = this.originalProducts.filter(p => p.id !== this.productToDelete?.id);
          this.filterProducts();
        },
        error: (err: any) => {
          console.error('Error deleting product', err);
          this.isLoading = false;
          this.showDeleteConfirmation = false;
          // Optionally show error toast
        }
      });
    }
  }

  cancelDelete(): void {
    this.showDeleteConfirmation = false;
    this.productToDelete = null;
  }

  updateProduct(product: Product): void {
    const url = `/updateProduct?productid=${product.codeBare}`;
    window.open(url, '_blank');
  }

  openPrintDialog(products: Product[]): void {
    this.dialog.open(PrintBarcodeDialogComponent, {
      data: { products },
      width: '600px',
      maxWidth: '96vw',
      panelClass: 'barcode-print-dialog'
    });
  }
}
